import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Search, Save, Sparkles, Plus, X, CheckCircle2, AlertCircle, Image as ImageIcon } from 'lucide-react';
import { api } from '@/lib/api';
import { TextField, Toggle, Select } from '@/components/admin/shared';

function TagsInput({ label, value = [], onChange, placeholder }) {
  const [text, setText] = useState('');
  const commit = (v) => {
    const t = (v ?? text).trim();
    if (!t) return;
    if (!value.includes(t)) onChange([...value, t]);
    setText('');
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div>
      {label && <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">{label}</label>}
      <div className="mt-1 pg-input rounded-[var(--pg-radius-sm)] px-2 py-1.5 flex flex-wrap gap-1 items-center">
        {value.map((t, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[rgba(226,199,122,0.14)] border border-[rgba(226,199,122,0.4)] text-[11px] text-[var(--pg-gold-2)]">
            {t}
            <button onClick={() => remove(i)} className="text-[var(--pg-ink-muted)] hover:text-[var(--pg-neon-pink)]">×</button>
          </span>
        ))}
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(); }
          }}
          onBlur={() => commit()}
          placeholder={placeholder || 'Type and press Enter'}
          className="bg-transparent flex-1 min-w-[8ch] text-xs px-1 py-0.5 focus:outline-none"
        />
      </div>
    </div>
  );
}

const VISIBILITY_OPTS = [
  { value: 'public', label: 'Public (indexed)' },
  { value: 'hidden', label: 'Hidden from search (still reachable by direct link)' },
];

/**
 * Shared Discovery Panel used across all content editors.
 * Props:
 *   contentType: 'product' | 'article' | 'audio_item' | 'video' | 'gallery_item' | 'mobile_read' | 'home_note' | 'custom'
 *   contentId:   string (persisted id; the panel disables itself while a new item has no id yet)
 *   defaultTitle?: string (used to prefill the search title on first open)
 *   defaultImage?: string (used to prefill featured image on first open)
 */
export default function DiscoveryPanel({ contentType, contentId, defaultTitle, defaultImage }) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState({ audiences: [], primary_topics: [] });
  const [form, setForm] = useState({
    search_title: '', search_description: '', slug: '',
    search_topics: [], audience: [], primary_topic: '',
    visibility: 'public', featured_image: '', related: [],
  });
  const [checklist, setChecklist] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [relatedOptions, setRelatedOptions] = useState({ channels: [], collections: [], series: [], content: [] });
  const [relatedFilter, setRelatedFilter] = useState('');
  const [msg, setMsg] = useState(null);

  const isDisabled = !contentId;

  const load = async () => {
    if (isDisabled) return;
    setBusy(true);
    try {
      const [cfgRes, docRes, optsRes] = await Promise.all([
        api.get('/admin/discovery/config'),
        api.get(`/admin/discovery/${contentType}/${contentId}`),
        api.get('/admin/discovery/related-options'),
      ]);
      setCfg(cfgRes.data);
      const doc = docRes.data || {};
      setForm({
        search_title: doc.search_title || defaultTitle || '',
        search_description: doc.search_description || '',
        slug: doc.slug || '',
        search_topics: doc.search_topics || [],
        audience: doc.audience || [],
        primary_topic: doc.primary_topic || '',
        visibility: doc.visibility || 'public',
        featured_image: doc.featured_image || defaultImage || '',
        related: doc.related || [],
      });
      setRelatedOptions(optsRes.data || {});
      await refreshChecklist();
      setLoaded(true);
    } catch (e) { setMsg(e?.response?.data?.detail || 'Failed to load Discovery'); }
    finally { setBusy(false); }
  };

  const refreshChecklist = async () => {
    if (isDisabled) return;
    try {
      const { data } = await api.get(`/admin/discovery/${contentType}/${contentId}/checklist`);
      setChecklist(data);
    } catch { /* ignore */ }
  };

  const loadSuggestions = async () => {
    if (isDisabled) return;
    try {
      const { data } = await api.get(`/admin/discovery/${contentType}/${contentId}/suggest-related`);
      setSuggestions(data?.suggestions || []);
    } catch { setSuggestions([]); }
  };

  useEffect(() => { if (open && !loaded) load(); }, [open, loaded]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleAudience = (a) => setForm((f) => ({
    ...f,
    audience: f.audience.includes(a) ? f.audience.filter((x) => x !== a) : [...f.audience, a],
  }));

  const addRelated = (opt) => setForm((f) => {
    const key = `${opt.kind}:${opt.ref_type || ''}:${opt.ref_id}`;
    const exists = (f.related || []).some((r) => `${r.kind}:${r.ref_type || ''}:${r.ref_id}` === key);
    if (exists) return f;
    return { ...f, related: [...(f.related || []), opt] };
  });
  const removeRelated = (idx) => setForm((f) => ({ ...f, related: (f.related || []).filter((_, i) => i !== idx) }));

  const save = async () => {
    if (isDisabled) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/discovery/${contentType}/${contentId}`, form);
      setForm({
        search_title: data.search_title || '', search_description: data.search_description || '',
        slug: data.slug || '', search_topics: data.search_topics || [],
        audience: data.audience || [], primary_topic: data.primary_topic || '',
        visibility: data.visibility || 'public', featured_image: data.featured_image || '',
        related: data.related || [],
      });
      setMsg('Discovery settings saved.');
      await refreshChecklist();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const allRelatedFlat = useMemo(() => {
    const flat = [
      ...(relatedOptions.channels || []),
      ...(relatedOptions.collections || []),
      ...(relatedOptions.series || []),
      ...(relatedOptions.content || []),
    ];
    if (!relatedFilter) return flat.slice(0, 40);
    const q = relatedFilter.toLowerCase();
    return flat.filter((o) => (o.label || '').toLowerCase().includes(q)).slice(0, 40);
  }, [relatedOptions, relatedFilter]);

  return (
    <div className="pg-admin-card rounded-[var(--pg-radius-md)] overflow-hidden" data-testid="discovery-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[rgba(242,238,227,0.03)] transition-colors"
        data-testid="discovery-panel-toggle"
      >
        <div className="flex items-center gap-2">
          <Search size={14} className="text-[var(--pg-gold-2)]" />
          <span className="pg-label text-[10px]">DISCOVERY</span>
          {checklist && (
            <span className="ml-2 text-[10px] font-mono tracking-widest uppercase"
                  style={{ color: checklist.status === 'ready' ? 'var(--pg-emerald-2)' : 'var(--pg-gold-2)' }}>
              {checklist.status === 'ready' ? 'Ready for Discovery' : 'Needs Attention'} · {checklist.strong}/{checklist.total}
            </span>
          )}
          {isDisabled && <span className="ml-2 text-[10px] font-mono text-[var(--pg-ink-faint)]">Save the item first to enable Discovery.</span>}
        </div>
        {open ? <ChevronUp size={16} className="text-[var(--pg-ink-muted)]" /> : <ChevronDown size={16} className="text-[var(--pg-ink-muted)]" />}
      </button>

      {open && !isDisabled && (
        <div className="p-4 border-t border-[var(--pg-border-2)] space-y-4">
          {msg && <div className="pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs text-[var(--pg-ink)] flex items-center justify-between">
            <span>{msg}</span>
            <button onClick={() => setMsg(null)}><X size={12} /></button>
          </div>}

          <TextField label="Search Title (auto-uses content title if left blank on public pages)" value={form.search_title} onChange={set('search_title')} />
          <TextField label="Search Description — write a plain-language 1–2 sentence summary" value={form.search_description} onChange={set('search_description')} textarea rows={3} />

          <div className="grid grid-cols-2 gap-3">
            <TextField label="URL slug (letters, numbers, dashes)" value={form.slug} onChange={set('slug')} placeholder="auto-generated from title if blank" />
            <Select label="Search Visibility" value={form.visibility} onChange={set('visibility')} options={VISIBILITY_OPTS} />
          </div>

          <TagsInput label="Search Topics — natural words a visitor would type" value={form.search_topics} onChange={set('search_topics')} placeholder="Detroit wall art, healing after divorce, Christian podcast…" />

          <div>
            <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Audience</label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(cfg.audiences || []).map((a) => {
                const active = form.audience.includes(a);
                return (
                  <button key={a} type="button" onClick={() => toggleAudience(a)}
                          data-testid={`discovery-audience-${a}`}
                          className="px-2.5 py-1 rounded-full border text-[11px] font-mono tracking-widest transition-colors"
                          style={{
                            background: active ? 'rgba(226,199,122,0.14)' : 'transparent',
                            borderColor: active ? 'rgba(226,199,122,0.6)' : 'var(--pg-border-2)',
                            color: active ? 'var(--pg-gold-2)' : 'var(--pg-ink-muted)',
                          }}>
                    {a}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Primary Topic</label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(cfg.primary_topics || []).map((t) => {
                const active = form.primary_topic === t;
                return (
                  <button key={t} type="button" onClick={() => set('primary_topic')(active ? '' : t)}
                          data-testid={`discovery-primary-${t}`}
                          className="px-2.5 py-1 rounded-full border text-[11px] font-mono tracking-widest transition-colors"
                          style={{
                            background: active ? 'rgba(25,194,142,0.14)' : 'transparent',
                            borderColor: active ? 'rgba(25,194,142,0.6)' : 'var(--pg-border-2)',
                            color: active ? 'var(--pg-emerald-2)' : 'var(--pg-ink-muted)',
                          }}>
                    {t}
                  </button>
                );
              })}
              <input
                placeholder="or type custom…"
                value={form.primary_topic}
                onChange={(e) => set('primary_topic')(e.target.value)}
                className="pg-input px-2.5 py-1 text-[11px] rounded-full w-40"
              />
            </div>
          </div>

          <TextField label="Featured image URL (for social sharing)" value={form.featured_image} onChange={set('featured_image')} placeholder="/media/…" />

          {/* Social preview */}
          <div className="pg-input rounded-[var(--pg-radius-sm)] p-3">
            <p className="pg-label text-[10px] mb-2">Social Preview</p>
            <div className="flex gap-3 items-start">
              {form.featured_image ? (
                <img src={form.featured_image} alt="" className="w-24 h-24 object-cover rounded" />
              ) : (
                <div className="w-24 h-24 rounded bg-[rgba(255,255,255,0.03)] flex items-center justify-center text-[var(--pg-ink-faint)]"><ImageIcon size={20} /></div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-serif text-sm text-[var(--pg-ink)] truncate">{form.search_title || defaultTitle || 'Untitled'}</p>
                <p className="text-[11px] text-[var(--pg-ink-muted)] mt-1 line-clamp-3">{form.search_description || 'Add a description to see how this content will appear when shared.'}</p>
                {form.slug && <p className="text-[10px] text-[var(--pg-ink-faint)] mt-1 font-mono">planet-g · /{form.slug}</p>}
              </div>
            </div>
          </div>

          {/* Related content */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Related Planet G Content</label>
              <button type="button" onClick={loadSuggestions} className="pg-btn-ghost px-2 py-1 rounded-[var(--pg-radius-sm)] text-[10px] flex items-center gap-1">
                <Sparkles size={12} /> Suggest
              </button>
            </div>
            {suggestions.length > 0 && (
              <div className="pg-input rounded-[var(--pg-radius-sm)] p-3 mb-2">
                <p className="text-[10px] font-mono text-[var(--pg-ink-faint)] mb-2">Smart suggestions (deterministic, no AI):</p>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.map((s, i) => (
                    <button key={i} type="button" onClick={() => addRelated(s)}
                            className="px-2 py-1 rounded-full border border-[rgba(226,199,122,0.4)] text-[11px] text-[var(--pg-gold-2)] hover:bg-[rgba(226,199,122,0.08)] flex items-center gap-1">
                      <Plus size={10} /> {s.label} <span className="text-[9px] text-[var(--pg-ink-faint)]">({s.score})</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="pg-input rounded-[var(--pg-radius-sm)] p-2 mb-2">
              <input value={relatedFilter} onChange={(e) => setRelatedFilter(e.target.value)} placeholder="Search channels, collections, or content…"
                     className="pg-input w-full px-2 py-1 text-xs rounded" />
              <div className="mt-2 max-h-40 overflow-y-auto space-y-0.5">
                {allRelatedFlat.map((o, i) => (
                  <button key={i} type="button" onClick={() => addRelated(o)}
                          className="w-full text-left px-2 py-1 rounded hover:bg-[rgba(242,238,227,0.04)] text-[11px] flex items-center justify-between">
                    <span className="text-[var(--pg-ink)] truncate">{o.label}</span>
                    <span className="text-[9px] font-mono text-[var(--pg-ink-faint)] uppercase tracking-widest">{o.kind}{o.ref_type ? ` · ${o.ref_type}` : ''}</span>
                  </button>
                ))}
                {allRelatedFlat.length === 0 && <p className="text-[10px] text-[var(--pg-ink-faint)] px-2 py-1">No matches.</p>}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(form.related || []).length === 0 && <p className="text-[11px] font-mono text-[var(--pg-ink-faint)]">No related items yet.</p>}
              {(form.related || []).map((r, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-[var(--pg-border-2)] text-[11px]" data-testid={`discovery-related-${r.ref_id}`}>
                  <span className="text-[var(--pg-ink)]">{r.label || r.ref_id}</span>
                  <span className="text-[9px] font-mono text-[var(--pg-ink-faint)] uppercase">{r.kind}{r.ref_type ? `·${r.ref_type}` : ''}</span>
                  <button onClick={() => removeRelated(i)} className="text-[var(--pg-ink-muted)] hover:text-[var(--pg-neon-pink)]"><X size={10} /></button>
                </span>
              ))}
            </div>
          </div>

          {/* Checklist */}
          {checklist && (
            <div className="pg-input rounded-[var(--pg-radius-sm)] p-3">
              <p className="pg-label text-[10px] mb-2">DISCOVERY CHECKLIST</p>
              <ul className="space-y-1">
                {checklist.checks.map((c) => (
                  <li key={c.key} className="text-[11px] font-mono flex items-center gap-2">
                    {c.ok
                      ? <CheckCircle2 size={12} className="text-[var(--pg-emerald-2)]" />
                      : <AlertCircle size={12} className="text-[var(--pg-gold-2)]" />}
                    <span className={c.ok ? 'text-[var(--pg-ink)]' : 'text-[var(--pg-ink-muted)]'}>{c.label}</span>
                  </li>
                ))}
              </ul>
              {checklist.recommendations?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-[var(--pg-border-2)] space-y-1">
                  {checklist.recommendations.map((r, i) => (
                    <p key={i} className="text-[10px] text-[var(--pg-ink-faint)]">• {r}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <button data-testid="discovery-save" onClick={save} disabled={busy} className="pg-btn-primary px-3 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2 disabled:opacity-60">
              <Save size={13} /> {busy ? 'Saving…' : 'Save Discovery'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
